import React, { useState } from "react"
import { Button, FloatingLabel, Form } from "react-bootstrap"
import { Link } from "react-router-dom"
import axios from "axios"
import { useFormik } from "formik"
import * as Yup from "yup"
import { admin } from "../../config/apiEndpoints"
import logo from "../../assets/images/logo.svg"
import { useCookies } from "react-cookie"
import { handleOnBlur, handleOnchange } from "../../lib/GlobalMethods"
import { toast } from "react-toastify"
import { useDispatch } from "react-redux"
import { addUserDetails } from "../../store/slices/basicInformationSlice"
import { addModulePermissions } from "../../store/slices/moduleSlice"
import { Spinner } from "react-bootstrap"

const Login = () => {
  const [cookie, setCookie] = useCookies(["authToken"])
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const togglePasswordVisibility = () => setShowPassword(!showPassword)
  const dispatch = useDispatch()

  const validationSchema = Yup.object({
    email: Yup.string()
      .required("Email is Required")
      .matches(
        /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
        "Please enter a valid email",
      ),
    password: Yup.string()
      .required("Password is Required")
      .min(8, "Password should be 8 chars minimum.")
      .max(16, "Password should be 16 chars maximum."),
  })

  const handleSubmit = async (values, { resetForm }) => {
    try {
      setIsLoading(true)
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}${admin.login}`,
        values,
      )
      if (response?.data?.code === 200) {
        toast.success(response.data.message)
        dispatch(
          addUserDetails({
            ...response?.data?.data,
            dataScope: response?.data?.dataScope,
          }),
        )
        dispatch(addModulePermissions(response?.data?.permissions))
        setCookie("authToken", `${response.data.accessToken}`, {
          path: "/",
        })
        resetForm()
      } else {
        throw new Error(
          response?.data?.message || "Unable to log in. Check your email and password.",
        )
      }
    } catch (error) {
      setIsLoading(false)
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to log in. Please try again.",
      )
    }
  }

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: handleSubmit,
  })

  const { errors, values, touched } = formik

  return (
    <>
      <div className="position-relative">
        <div className="login_screen_vh">
          <div className="login_bg d-flex align-items-center justify-content-center">
            <div className="login_screen shadow-sm">
              <img src={logo} alt="MegaCharge" className="logo_login mb-3" />
              <h1 className="fs-4 mb-4">Login</h1>
              <Form noValidate onSubmit={formik.handleSubmit}>
                <FloatingLabel
                  controlId="floatingInput"
                  label={
                    <>
                      Email Address <span className="required_star">*</span>
                    </>
                  }
                  className="mb-3"
                >
                  <Form.Control
                    autoComplete="false"
                    type="email"
                    name="email"
                    placeholder=""
                    className="bg-white mb-n1"
                    value={values.email}
                    onChange={(e) => handleOnchange(e, false, formik)}
                    onBlur={(e) => handleOnBlur(e, formik)}
                    isInvalid={touched.email && errors.email}
                    isValid={touched.email && !errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </FloatingLabel>
                <FloatingLabel
                  controlId="floatingPassword"
                  label={
                    <>
                      Password <span className="required_star">*</span>
                    </>
                  }
                  className="mb-2 position-relative passwordicon_wrapper"
                >
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    autoComplete="false"
                    placeholder=""
                    name="password"
                    className="bg-white"
                    value={values.password}
                    onChange={(e) => handleOnchange(e, false, formik)}
                    onBlur={(e) => handleOnBlur(e, formik)}
                    isInvalid={touched.password && !!errors.password}
                    isValid={touched.password && !errors.password}
                    maxLength={16}
                  />
                  <i
                    className={`icon_main ${
                      showPassword ? "eye_close" : "eye_open"
                    } `}
                    onClick={togglePasswordVisibility}
                  ></i>
                  <Form.Control.Feedback type="invalid">
                    {errors.password}
                  </Form.Control.Feedback>
                </FloatingLabel>

                <div className="align-items-center d-flex justify-content-end mb-3">
                  <Link
                    className="link-secondary link-offset-2 link-underline-opacity-0 link-underline-opacity-100-hover"
                    to="/forgot-password"
                  >
                    Forgot Password?
                  </Link>
                </div>

                <Button
                  variant="primary"
                  type="submit"
                  className="w-100"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Spinner size="sm" animation="border" variant="white" />
                  ) : (
                    "Login"
                  )}
                </Button>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Login
